<template>
  <AdminLayout>
    <Head title="Transfers" />

    <template #pageTitle> Transfers </template>

    <template #pageTitleWidgets>
      <DataGridControlBar :show-filter-reset="false" />
    </template>

    <template #headingOptions>
      <button
        @click="openCreate(transferMethod)"
        type="button"
        class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        Create Transfer
      </button>
    </template>

    <div class="w-full flex flex-col h-full overflow-y-auto">
      <div class="flex gap-1 py-2 px-4 sm:px-12">
        <div class="w-20 md:w-24">
          <SavedFiltersMenu
            :saved-filters="usersSavedFilters.data.value"
            @save="({ filterName }) => usersSavedFilters.save({ filterName })"
            @load="usersSavedFilters.load"
            @delete="usersSavedFilters.remove"
            @clear-all="resetTableToDefault"
          />
        </div>
        <div>
          <SavedFiltersReset
            :active="!stickyFilters.isInDefaultState.value"
            @clear-all="resetTableToDefault"
          />
        </div>
      </div>
      <DataGrid
        name="transfers_data_table"
        :endpoint="route('api.tables.transfers')"
        :filters="form"
        :columns="columns"
        :block-size="40"
        :page-size="40"
        :initial-state="gridInitState"
      />

      <!-- Single transfer view -->
      <TransferPanel
        :open="open"
        :selected-transfer="selectedTransfer"
        :default-location-name="defaultLocationName"
        :errors="errors"
        @close="closeTransfer"
        @setDrivers="openSetDrivers"
        @setVehicle="openSetVehicle"
        @openReceive="openReceive"
        @openEdit="openEdit"
        @openPrintLabels="openPrintLabels"
      />
      <!-- END Single transfer view -->
    </div>

    <template #modals>
      <CreateStandardModal
        v-model:open="showingStandardCreateModal"
        :locations="transferrableLocations"
        :spaces="spaces.data"
        @created="transferCreated"
      />
      <CreateSubLotModal
        v-model:open="showingSubLotCreateModal"
        :locations="transferrableLocations"
        :spaces="spaces.data"
        @created="transferCreated"
      />

      <EditStandardModal
        v-model:open="showingStandardEditModal"
        :locations="locations.data"
        :spaces="spaces.data"
        :transfer="editingTransfer"
        @created="closeEdit"
      />
      <EditSubLotModal
        v-model:open="showingSubLotEditModal"
        :locations="locations.data"
        :spaces="spaces.data"
        :transfer="editingTransfer"
        @updated="closeEdit"
      />

      <SetDriversModal
        v-model:open="showingSetDriversModal"
        :action-text="'Save Drivers'"
        :transfer="setDriversTransfer"
        :locations="locations.data"
        :drivers="drivers.data"
        @saved="closeSetDrivers()"
      />

      <SetVehicleModal
        v-model:open="showingSetVehicleModal"
        :action-text="'Save Vehicle'"
        :transfer="setVehicleTransfer"
        :locations="locations.data"
        :vehicles="vehicles.data"
        @saved="closeSetVehicle()"
      />

      <StandardReceiveModal
        v-model:open="showingStandardReceiveModal"
        :action-text="'Receive'"
        :transfer="receivingTransfer"
        :spaces="spaces.data"
        @received="closeReceive()"
        :adjustment-reasons="adjustmentReasons.data"
      />
      <SubLotReceiveModal
        v-model:open="showingSubLotReceiveModal"
        :action-text="'Receive'"
        :transfer="receivingTransfer"
        :spaces="spaces.data"
        @received="closeReceive()"
        :adjustment-reasons="adjustmentReasons.data"
      />

      <print-label
        :show="showPrintLabelModal"
        :packages="selectedTransfer?.packages || []"
        :locations="locations.data"
        :label-location-id="selectedTransfer?.destination_id"
        :modes="labelModes"
        @close="closePrintLabels()"
        :show-only-new-sub-lots="true"
      />
    </template>
  </AdminLayout>
</template>

<script setup>
import { ref, onBeforeMount, watch, computed, readonly } from 'vue'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, useForm } from '@inertiajs/vue3'
import CreateStandardModal from '@/Pages/Transfers/Components/Standard/CreateModal'
import CreateSubLotModal from '@/Pages/Transfers/Components/SubLot/CreateModal'
import EditStandardModal from '@/Pages/Transfers/Components/Standard/CreateModal'
import EditSubLotModal from '@/Pages/Transfers/Components/SubLot/EditModal'
import SetDriversModal from '@/Pages/Transfers/Components/SetDriversModal'
import StandardReceiveModal from '@/Pages/Transfers/Components/Standard/ReceiveModal'
import SubLotReceiveModal from '@/Pages/Transfers/Components/SubLot/ReceiveModal'
import _datetime from '@/Filters/DateTime'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import TransferPanel from '@/Pages/Transfers/Components/TransferPanel.vue'
import { useAccessCompany } from '@/Composables/access-company'
import _ from 'lodash'
import { DateTime } from 'luxon'
import BadgeStatusCell from '@/Components/DataGrid/Cells/BadgeStatusCell.vue'
import ClickableCell from '@/Components/DataGrid/Cells/ClickableCell.vue'
import PrintLabel from '@/Pages/Packages/Components/PrintLabel'
import SetVehicleModal from '@/Pages/Transfers/Components/SetVehicleModal.vue'
import SavedFiltersMenu from '@/Components/DataGrid/Filters/SavedFiltersMenu.vue'
import { updateColumnSort } from '@/Components/DataGrid/utils'
import { useUsersSavedFilters, useStickyFilters } from '@/Components/DataGrid/Composables/saved-filters'
import SavedFiltersReset from '@/Components/DataGrid/Filters/SavedFiltersReset.vue'
import DataGridControlBar from '@/Components/DataGrid/Controls/ControlBar.vue'
import { wrapWithFlag, createFlagColumn } from '@/Components/Flags'

const SAVED_FILTERS_KEY = 'transfers_standard'

// Props
const props = defineProps({
  params: Object,
  transfer: Object,
  locations: Object,
  spaces: Object,
  statuses: Object,
  drivers: Object,
  vehicles: Object,
  adjustmentReasons: Object,
  errors: Object,
  labelModes: Array,
  transferMethod: String,
  types: Array
})

const { context } = useInitGridState()
const gridApi = computed(() => context.gridApi)

const usersSavedFilters = useUsersSavedFilters({
  key: SAVED_FILTERS_KEY,
  gridApi: readonly(gridApi)
})

const stickyFilters = useStickyFilters({
  key: SAVED_FILTERS_KEY,
  gridApi: readonly(gridApi)
})

// Load the sticky filter when the grid is initialized
watch(
  gridApi,
  async () => {
    await stickyFilters.load()
  },
  { once: true }
)

// Persist the filter state to storage any time it changes.
watch(
  [() => context.activeFilters, () => context.activeSort],
  () => {
    stickyFilters.save()
  },
  {
    deep: true
  }
)

const resetTableToDefault = () => {
  gridApi.value.setFilterModel({
    display_status: {
      filterType: 'set',
      values: ['pending', 'processing', 'processed', 'in_transit']
    }
  })
  updateColumnSort(gridApi, columns.value)
}

/**
 * Individual transfer view methods
 */
const showTransfer = (transfer) => {
  reload(transfer.id)
}

// Data
const open = ref(false)
const showingCreateModal = ref(false)
const showingEditModal = ref(false)
const showingDriversModal = ref(false)
const showingVehicleModal = ref(false)
const showingReceiveModal = ref(false)
const showingMiscountModal = ref(false)
const showPrintLabelModal = ref(false)
const editingTransfer = ref(null)
const receivingTransfer = ref(null)
const form = useForm({})
const { defaultLocationName } = useAccessCompany()

const columns = ref([
  {
    headerName: 'Transfer ID',
    field: 'reference_id',
    flex: 1,
    minWidth: 190,
    cellRenderer: wrapWithFlag(ClickableCell),
    cellRendererParams: (params) => ({
      showFlag: Boolean(params.data.flags?.length),
      clickAction: showTransfer,
      valueFormatter: (params) => {
        if (params.data.parent_id && params.data.type === 'Return') {
          return params.value + ' ← ' + params.data.parent.reference_id
        }
        return params.value
      }
    })
  },
  {
    headerName: 'Origin',
    field: 'origin',
    valueFormatter: (params) => params.value?.name,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.locations.data,
      keyCreator: (params) => params.value?.id,
      valueFormatter: (params) => params.value?.name,
      suppressSorting: true
    }
  },
  {
    headerName: 'Destination',
    field: 'destination',
    valueFormatter: (params) => params.value?.name,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.locations.data,
      keyCreator: (params) => params.value?.id,
      valueFormatter: (params) => params.value?.name,
      suppressSorting: true
    }
  },
  {
    headerName: 'Type',
    field: 'type',
    valueFormatter: (params) => params.value,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.types,
      valueFormatter: (params) => _.capitalize(params.value),
      suppressSorting: true
    },
    sortable: false
  },
  {
    headerName: '# Prod',
    field: 'packages_count'
  },
  {
    headerName: 'Total QTY',
    field: 'packages_sum_quantity'
  },
  {
    headerName: 'Date Initiated',
    field: 'transferred_at',
    sort: 'desc',
    valueFormatter: (params) => _datetime(params.value, DateTime.DATETIME_SHORT)
  },
  {
    headerName: 'Status',
    field: 'display_status',
    cellRenderer: BadgeStatusCell,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: _.map(props.statuses, (status, key) => ({ value: key, label: status })),
      keyCreator: (params) => params.value.value,
      valueFormatter: (params) => params.value.label,
      suppressSorting: true
    }
  },
  {
    headerName: 'Date Sent',
    field: 'sent_at',
    valueFormatter: (params) => (params.value ? _datetime(params.value, DateTime.DATETIME_SHORT) : null)
  },
  {
    headerName: 'Date Received',
    field: 'received_at',
    valueFormatter: (params) => (params.value ? _datetime(params.value, DateTime.DATETIME_SHORT) : null)
  },
  createFlagColumn()
])

const gridInitState = ref({
  filter: {
    filterModel: {
      display_status: {
        filterType: 'set',
        values: ['pending', 'processing', 'in_transit']
      }
    }
  }
})

// Computed
const selectedTransfer = computed(() => {
  if (!props.transfer) {
    return null
  }

  return props.transfer.data
})

const transferrableLocations = computed(() => {
  return props.locations.data.filter((location) => location.settings.can_transfer)
})

const setDriversTransfer = computed(() => selectedTransfer.value)
const setVehicleTransfer = computed(() => selectedTransfer.value)

const showingStandardCreateModal = computed({
  get() {
    return showingCreateModal.value && props.transferMethod === 'standard'
  },
  set(newValue) {
    showingCreateModal.value = newValue
  }
})
const showingSubLotCreateModal = computed({
  get() {
    return showingCreateModal.value && props.transferMethod === 'sub_lot'
  },
  set(newValue) {
    showingCreateModal.value = newValue
  }
})
const showingStandardEditModal = computed({
  get() {
    return showingEditModal.value && editingTransfer.value?.method === 'standard'
  },
  set(newValue) {
    showingEditModal.value = newValue

    if (!newValue) {
      closeEdit()
    }
  }
})
const showingSubLotEditModal = computed({
  get() {
    return showingEditModal.value && editingTransfer.value?.method === 'sub_lot'
  },
  set(newValue) {
    showingEditModal.value = newValue

    if (!newValue) {
      closeEdit()
    }
  }
})
const showingSetDriversModal = computed({
  get() {
    return showingDriversModal.value
  },
  set(newValue) {
    showingDriversModal.value = newValue

    if (!newValue) {
      closeSetDrivers()
    }
  }
})
const showingSetVehicleModal = computed({
  get() {
    return showingVehicleModal.value
  },
  set(newValue) {
    showingVehicleModal.value = newValue

    if (!newValue) {
      closeSetVehicle()
    }
  }
})
const showingStandardReceiveModal = computed({
  get() {
    return showingReceiveModal.value && receivingTransfer.value?.method === 'standard'
    // return showingReceiveModal.value && transferMethod === 'standard';
  },
  set(newValue) {
    showingReceiveModal.value = newValue

    if (!newValue) {
      closeReceive()
    }
  }
})
const showingSubLotReceiveModal = computed({
  get() {
    return showingReceiveModal.value && receivingTransfer.value?.method === 'sub_lot'
    // return showingReceiveModal.value && transferMethod === 'sub_lot';
  },
  set(newValue) {
    showingReceiveModal.value = newValue

    if (!newValue) {
      closeReceive()
    }
  }
})

// Methods
const reload = (transferId = null) => {
  const data = transferId ? { transfer: transferId } : {}
  useForm(data).get(route('transfers.index'))
}

const openCreate = (method) => {
  console.log('open with method', method)
  showPrintLabelModal.value = false
  showingCreateModal.value = true
  // props.transferMethod = method;
  showingEditModal.value = false
  open.value = false
  showingReceiveModal.value = false
  showingMiscountModal.value = false
}
const openEdit = (transfer) => {
  showPrintLabelModal.value = false
  editingTransfer.value = transfer
  showingCreateModal.value = true
  showingEditModal.value = true
  open.value = false
  showingReceiveModal.value = false
  showingMiscountModal.value = false
}

const openPrintLabels = () => {
  showPrintLabelModal.value = true
  showingCreateModal.value = false
  showingEditModal.value = false
  open.value = false
  showingReceiveModal.value = false
  showingMiscountModal.value = false
}
// Drivers for transfer
const openSetDrivers = () => {
  showingDriversModal.value = true
  open.value = false
}
// Vehicle for transfer
const openSetVehicle = () => {
  showingVehicleModal.value = true
  open.value = false
}

// Receive transfer
const openReceive = () => {
  receivingTransfer.value = selectedTransfer.value
  showPrintLabelModal.value = false
  showingCreateModal.value = false
  open.value = false
  showingReceiveModal.value = true
  showingMiscountModal.value = false
}

const openTransferView = () => {
  setTimeout(function () {
    open.value = true
  }, 300)
}

const closePrintLabels = () => {
  showPrintLabelModal.value = false
  showingCreateModal.value = false
  showingEditModal.value = false
  open.value = true
  showingReceiveModal.value = false
  showingMiscountModal.value = false
}
const closeCreate = () => {
  showingCreateModal.value = false
}
const transferCreated = () => {
  closeCreate()
  reload(null)
}
const closeEdit = () => {
  showingEditModal.value = false
  open.value = true
}
const closeSetDrivers = () => {
  showingDriversModal.value = false

  setTimeout(() => {
    if (selectedTransfer.value) {
      open.value = true
    }
  }, 500)
}
const closeSetVehicle = () => {
  showingVehicleModal.value = false

  setTimeout(() => {
    if (selectedTransfer.value) {
      open.value = true
    }
  }, 500)
}
const closeReceive = () => {
  receivingTransfer.value = null

  showingReceiveModal.value = false

  setTimeout(() => {
    if (selectedTransfer.value) {
      open.value = true
    }
  }, 500)
}

const closeTransfer = () => {
  open.value = false
  reload(null)
}

const canEdit = (transfer) => {
  return transfer.status === 'pending'
}

// Watchers
watch(showingCreateModal, (newVal) => {
  if (!newVal && editingTransfer.value) {
    closeCreate()
  }
})

onBeforeMount(() => {
  if (props.transfer) {
    openTransferView()
  } else {
    open.value = false
  }
})
</script>
